import clsx from "clsx";
import { Warning } from "@phosphor-icons/react";
import type { CohortReport } from "./quizReportApi";

/** Sınıfın modül sonu pratik durumu: kim çözmedi, ortalama ne, ortak zayıflık ne. */
export default function CohortPanel({ report }: { report: CohortReport }) {
  const solved = report.students.filter((s) => s.attempts > 0);
  const notSolved = report.students.filter((s) => s.attempts === 0);
  const avg =
    solved.length > 0
      ? Math.round(solved.reduce((s, x) => s + (x.avg_score ?? 0), 0) / solved.length)
      : null;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Çözen" value={`${solved.length}/${report.students.length}`} />
        <Stat label="Sınıf ortalaması" value={avg === null ? "—" : `%${avg}`} />
        <Stat label="Hiç çözmeyen" value={String(notSolved.length)} tone={notSolved.length > 0 ? "warn" : undefined} />
      </div>

      {report.weakTopics.length > 0 && (
        <div className="bg-myGb/20 rounded-2xl p-4">
          <div className="flex items-center gap-2 font-semibold mb-1">
            <Warning size={16} weight="bold" className="text-myRed" />
            Sınıfın ortak zayıf konuları
          </div>
          <p className="text-xs text-white/40 mb-3">
            En az 5 kez sorulmuş konular, başarı oranına göre sıralı. Derste tekrar
            edilmesi en çok işe yarayacak yerler.
          </p>
          <ul className="flex flex-col gap-2">
            {report.weakTopics.map((t) => {
              const ratio = t.asked > 0 ? t.correct / t.asked : 0;
              return (
                <li key={t.code} className="flex items-center gap-3 text-sm">
                  <span className="flex-1 text-white/80">{t.title}</span>
                  <div className="w-32 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className={clsx(
                        "h-full rounded-full",
                        ratio >= 0.7 ? "bg-myGreen" : ratio >= 0.4 ? "bg-white/60" : "bg-myRed",
                      )}
                      style={{ width: `${Math.round(ratio * 100)}%` }}
                    />
                  </div>
                  <span className="text-white/40 text-xs w-16 text-right">
                    {t.correct}/{t.asked}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="bg-myGb/20 rounded-2xl p-4 overflow-x-auto">
        <div className="font-semibold mb-3">Öğrenciler</div>
        <table className="w-full text-sm min-w-[480px]">
          <thead>
            <tr className="text-white/40 text-xs text-left">
              <th className="pb-1 font-medium">Öğrenci</th>
              <th className="pb-1 font-medium w-20 text-right">Deneme</th>
              <th className="pb-1 font-medium w-24 text-right">Ortalama</th>
              <th className="pb-1 font-medium w-28 text-right">Son çözüm</th>
            </tr>
          </thead>
          <tbody>
            {report.students.map((s) => (
              <tr key={s.user_id} className="border-t border-white/5">
                <td className="py-1.5 text-white/80">
                  {s.first_name} {s.last_name}
                </td>
                <td
                  className={clsx(
                    "text-right",
                    s.attempts === 0 ? "text-myRed font-semibold" : "text-white/60",
                  )}
                >
                  {s.attempts}
                </td>
                <td className="text-right text-white/60">
                  {s.avg_score === null ? "—" : `%${s.avg_score}`}
                </td>
                <td className="text-right text-white/40 text-xs">
                  {s.last_at ? new Date(s.last_at).toLocaleDateString("tr-TR") : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "warn" }) {
  return (
    <div className="bg-myGb/20 rounded-2xl p-4">
      <div className="text-xs text-white/40">{label}</div>
      <div className={clsx("text-2xl font-semibold mt-1", tone === "warn" && "text-myRed")}>
        {value}
      </div>
    </div>
  );
}
